/**
 * The workspace home, `/workspaces/:id` (spec M1-B1 §7.1, measured in `[c:workspace-home]`).
 *
 * Rebuilt in round 9j from the capture rather than from memory (P-039). The reference, at
 * 1440 × 900 with the pane open, is:
 *
 *   banner  main-area full bleed, **199 px**, `#f5f6f8`
 *   avatar  100 × 100, radius 20, 4 px white ring, overlapping the banner by 33 px
 *   title   32/500 at x 543 · description 16/22 under it · ⓘ after the title
 *   right   members stack 44 × 32 · **Invite / N** 69 × 32 `#0073ea` · `···` 32 × 32
 *   tabs    40 px, 16 px labels, padding 0 30, 2 px track, active underline `#0073ea`
 *   rows    57 px pitch, 1 px `#d0d4e4` separators
 *
 * Gone with it: the bare **Delete workspace** button that used to sit in the header. Deleting
 * a workspace is a `···` entry on monday and is refused outright for the main workspace —
 * see `WorkspaceHomeMenu` and `services/workspaces.ts`.
 *
 * monday's **Feedback** and **Agents** buttons stay absent (§3.2).
 */
import { Icon, WorkspaceAvatar, icons } from '@elizmonday/ui';
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useBootstrap, useSidebarData } from '../shell/bootstrap';
import { boardUrl } from '../shell/urls';
import { useShellStore } from '../shell/store';
import { CreateBoardDialog } from './CreateBoardDialog';
import { WorkspaceHomeMenu } from './WorkspaceHomeMenu';
import { WorkspaceMembers } from './WorkspaceMembers';
import { useFavorite } from './favorites';
import './boards.css';
import { useRecentBoards, useWorkspace, useWorkspaceMembers, useWorkspaceMutations } from './workspaces';

const TABS = ['Recents', 'Content', 'Collaborators', 'Permissions'] as const;
type Tab = (typeof TABS)[number];

/** The stack shows at most this many faces; the rest become a `+N` chip, as monday does. */
const MAX_FACES = 4;

export function WorkspaceHome({ workspaceId }: { workspaceId: string }) {
  const workspace = useWorkspace(workspaceId);
  const members = useWorkspaceMembers(workspaceId);
  const sidebar = useSidebarData(workspaceId);
  const recents = useRecentBoards(10);
  const bootstrap = useBootstrap();
  const mutations = useWorkspaceMutations();
  const openOverlay = useShellStore((state) => state.openOverlay);

  const [tab, setTab] = useState<Tab>('Recents');
  const [creating, setCreating] = useState(false);
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null);
  const [editingDescription, setEditingDescription] = useState(false);
  const moreRef = useRef<HTMLButtonElement>(null);

  const name = workspace.data?.name ?? '';
  const description = workspace.data?.description ?? '';
  const boards = (sidebar.data?.boards ?? []).filter((board) => board.state === 'active');
  const folders = sidebar.data?.folders ?? [];
  const recentHere = (recents.data ?? []).filter((board) => board.workspaceId === workspaceId);

  const memberRows = members.data ?? [];
  const viewerId = bootstrap.data?.me.id ?? null;
  const viewerIsMember = viewerId !== null && memberRows.some((row) => row.userId === viewerId);

  /** Content is the workspace's folders **and** boards, monday's own grouping (B1 §7.1). */
  const contentGroups = useMemo(() => {
    const byFolder = new Map<string | null, { id: string; name: string }[]>();
    for (const board of boards) {
      const key = board.folderId ?? null;
      const list = byFolder.get(key) ?? [];
      list.push({ id: board.id, name: board.name });
      byFolder.set(key, list);
    }
    return [
      { folderId: null, folderName: null, rows: byFolder.get(null) ?? [] },
      ...folders.map((folder) => ({
        folderId: folder.id,
        folderName: folder.name,
        rows: byFolder.get(folder.id) ?? [],
      })),
    ].filter((group) => group.rows.length > 0);
  }, [boards, folders]);

  return (
    <div className="ez-workspace-home" data-testid="workspace-home">
      {/* The banner runs the whole width of main and the avatar sits over its bottom edge. */}
      <div className="ez-workspace-home__banner" data-testid="workspace-banner" />

      <header className="ez-workspace-home__head">
        <span className="ez-workspace-home__avatar">
          <WorkspaceAvatar name={name} size={100} data-testid="workspace-avatar" />
        </span>

        <div className="ez-workspace-home__identity">
          <h1 className="ez-workspace-home__title" data-testid="workspace-title">
            <span className="ez-workspace-home__name">{name}</span>
            <button
              type="button"
              className="ez-workspace-home__info"
              aria-label="Workspace information"
              data-testid="workspace-info"
              title={
                workspace.data?.isDefault === true
                  ? 'The main workspace of this account. It cannot be deleted, left or retyped.'
                  : `${workspace.data?.kind === 'closed' ? 'Closed' : 'Open'} workspace · ${String(memberRows.length)} members`
              }
            >
              <Icon icon={icons.info} size={16} ignoreFocusStyle />
            </button>
          </h1>

          {editingDescription ? (
            <textarea
              className="ez-workspace-home__description-input"
              aria-label="Workspace description"
              defaultValue={description}
              data-testid="workspace-description-input"
              autoFocus
              onBlur={(event) => {
                setEditingDescription(false);
                if (event.target.value !== description) {
                  mutations.update.mutate({ id: workspaceId, description: event.target.value });
                }
              }}
            />
          ) : (
            <button
              type="button"
              className="ez-workspace-home__description"
              data-testid="workspace-description"
              onClick={() => setEditingDescription(true)}
            >
              {description === '' ? 'Add workspace description' : description}
            </button>
          )}
        </div>

        <div className="ez-workspace-home__actions">
          <button
            type="button"
            className="ez-workspace-home__members"
            aria-label="Workspace members"
            data-testid="workspace-members"
            onClick={() => setTab('Collaborators')}
          >
            {memberRows.slice(0, MAX_FACES).map((row) => (
              <span
                key={row.id}
                className="ez-workspace-home__face"
                // `color` is the server's derived per-person tint; the token is the fallback.
                style={{ background: row.color ?? 'var(--ui-border-color)' }}
                title={row.name ?? ''}
              >
                {(row.name ?? '?').trim().charAt(0).toUpperCase()}
              </span>
            ))}
            {memberRows.length > MAX_FACES ? (
              <span className="ez-workspace-home__face ez-workspace-home__face--rest">
                {`+${String(memberRows.length - MAX_FACES)}`}
              </span>
            ) : null}
          </button>

          <button
            type="button"
            className="ez-workspace-home__invite"
            data-testid="workspace-invite"
            onClick={() => openOverlay('invite')}
          >
            {`Invite / ${String(memberRows.length)}`}
          </button>

          <button
            type="button"
            ref={moreRef}
            className="ez-workspace-home__more"
            aria-label="Workspace options"
            data-testid="workspace-menu"
            onClick={() => {
              const box = moreRef.current?.getBoundingClientRect();
              setMenuAt({ x: box ? box.right - 220 : 0, y: box ? box.bottom + 4 : 0 });
            }}
          >
            <Icon icon={icons.moreActions} size={20} ignoreFocusStyle />
          </button>
        </div>
      </header>

      <div className="ez-workspace-home__tabs" role="tablist" aria-label="Workspace">
        {TABS.map((entry) => (
          <button
            key={entry}
            type="button"
            role="tab"
            aria-selected={tab === entry}
            // §7.1: Permissions is disabled until M8.P1 — greyed in the reference too.
            disabled={entry === 'Permissions'}
            data-testid={`workspace-tab-${entry.toLowerCase()}`}
            onClick={() => setTab(entry)}
          >
            {entry}
          </button>
        ))}
      </div>

      {tab === 'Recents' ? (
        <BoardList
          rows={recentHere.map((board) => ({ id: board.id, name: board.name }))}
          emptyLabel="Nothing opened here yet"
          onAddBoard={() => setCreating(true)}
        />
      ) : null}
      {tab === 'Content' ? (
        contentGroups.length === 0 ? (
          <BoardList rows={[]} emptyLabel="This workspace has no boards yet" onAddBoard={() => setCreating(true)} />
        ) : (
          <div data-testid="workspace-content">
            {contentGroups.map((group) => (
              <section key={group.folderId ?? 'root'}>
                {group.folderName === null ? null : (
                  <h2 className="ez-workspace-home__folder" data-testid={`workspace-folder-${group.folderId ?? ''}`}>
                    <Icon icon={icons.folder} size={16} ignoreFocusStyle />
                    {group.folderName}
                  </h2>
                )}
                <BoardList rows={group.rows} emptyLabel="" onAddBoard={() => setCreating(true)} />
              </section>
            ))}
          </div>
        )
      ) : null}
      {tab === 'Collaborators' ? <WorkspaceMembers workspaceId={workspaceId} /> : null}

      {menuAt ? (
        <WorkspaceHomeMenu
          workspaceId={workspaceId}
          viewerId={viewerId}
          viewerIsMember={viewerIsMember}
          x={menuAt.x}
          y={menuAt.y}
          onClose={() => setMenuAt(null)}
        />
      ) : null}
      {creating ? (
        <CreateBoardDialog workspaceId={workspaceId} onClose={() => setCreating(false)} />
      ) : null}
    </div>
  );
}

/** One row per board: icon · name · the per-viewer favourite star (§7.2). */
function BoardList({
  rows,
  emptyLabel,
  onAddBoard,
}: {
  rows: { id: string; name: string }[];
  emptyLabel: string;
  onAddBoard(): void;
}) {
  if (rows.length === 0) {
    if (emptyLabel === '') return null;
    return (
      <div className="ez-workspace-home__empty" data-testid="workspace-empty">
        <p>{emptyLabel}</p>
        <button type="button" className="ez-board-dialog__save" onClick={onAddBoard}>
          Add board
        </button>
      </div>
    );
  }
  return (
    <ul className="ez-workspace-home__list" data-testid="workspace-board-list">
      {rows.map((row) => (
        <BoardRow key={row.id} id={row.id} name={row.name} />
      ))}
    </ul>
  );
}

function BoardRow({ id, name }: { id: string; name: string }) {
  const favorite = useFavorite('board', id);
  return (
    <li className="ez-workspace-home__row" data-testid={`workspace-board-${id}`}>
      <Icon icon={icons.board} size={16} ignoreFocusStyle />
      <Link to={boardUrl(id)}>{name}</Link>
      <button
        type="button"
        aria-pressed={favorite.isFavorite}
        aria-label={favorite.isFavorite ? `Unfavourite ${name}` : `Favourite ${name}`}
        data-testid={`workspace-board-favorite-${id}`}
        onClick={() => favorite.toggle()}
      >
        <Icon icon={favorite.isFavorite ? icons.favoriteFilled : icons.favorite} size={16} ignoreFocusStyle />
      </button>
    </li>
  );
}
